import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    CircularProgress,
    Alert
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ClipboardIcon from '@mui/icons-material/Assignment';
import {
    OverviewStatsChart,
    JobsVsApplicationsChart,
    MonthlyJobsChart,
    InvoiceStatusChart,
    ApplicationsOverTimeChart
} from '../components/DashboardCharts';

function EmployerDashboard() {
    const { data: company, isLoading: companyLoading } = useQuery(
        'myCompany',
        () => JobHttpService.loadMyCompany()
    );

    const { data: jobsData } = useQuery(
        ['employerJobs', company?.id],
        () => company?.id
            ? JobHttpService.loadJobs({ filters: { company: company.id }, size: 1000 })
            : { results: [], count: 0 },
        { enabled: !!company?.id }
    );

    const { data: employeesData } = useQuery(
        ['employees', company?.id],
        () => company?.id
            ? JobHttpService.loadEmployees({ company: company.id })
            : { results: [], count: 0 },
        { enabled: !!company?.id }
    );

    const { data: salarySpecsData } = useQuery(
        ['salarySpecifications', company?.id],
        () => company?.id
            ? JobHttpService.loadSalarySpecifications({ company: company.id })
            : { results: [], count: 0 },
        { enabled: !!company?.id }
    );

    const { data: invoicesData } = useQuery(
        ['invoices', company?.id],
        () => company?.id
            ? JobHttpService.loadInvoices({ company: company.id })
            : { results: [], count: 0 },
        { enabled: !!company?.id }
    );

    const { data: contractsData } = useQuery(
        ['contracts', company?.id],
        () => company?.id
            ? JobHttpService.loadContracts({ company: company.id })
            : { results: [], count: 0 },
        { enabled: !!company?.id }
    );

    const jobs = jobsData?.results || [];
    const jobIds = jobs.map(j => j.id);

    const { data: applicationsData } = useQuery(
        ['allApplications', jobIds.join(',')],
        async () => {
            if (!jobIds.length) return { results: [], count: 0 };
            try {
                const response = await JobHttpService.loadJobApplications({ size: 1000 });
                const all = response?.results || response || [];
                const filtered = all.filter(app => jobIds.includes(app.job?.id || app.job));
                return { results: filtered, count: filtered.length };
            } catch {
                return { results: [], count: 0 };
            }
        },
        { enabled: !!company?.id && jobIds.length > 0, staleTime: 60000 }
    );

    const employees = employeesData?.results || employeesData || [];
    const invoices = invoicesData?.results || invoicesData || [];
    const salarySpecs = salarySpecsData?.results || salarySpecsData || [];
    const contracts = contractsData?.results || contractsData || [];
    const applications = applicationsData?.results || applicationsData || [];

    const quickLinks = useMemo(() => [
        { label: 'My Company', icon: BusinessIcon, path: '/employer/my-company', subtitle: 'Manage company details' },
        { label: 'Jobs', icon: WorkIcon, path: '/employer/jobs', subtitle: `${jobs.length} job${jobs.length !== 1 ? 's' : ''} posted` },
        { label: 'Applications', icon: ClipboardIcon, path: '/employer/applications', subtitle: `${applications.length} application${applications.length !== 1 ? 's' : ''}` },
        { label: 'Employees', icon: PeopleIcon, path: '/employer/employees', subtitle: `${employees.length} employee${employees.length !== 1 ? 's' : ''}` },
        { label: 'Salary Specification', icon: AccountBalanceIcon, path: '/employer/salary-specification', subtitle: `${salarySpecs.length} specification${salarySpecs.length !== 1 ? 's' : ''}` },
        { label: 'Contracts', icon: DescriptionIcon, path: '/employer/contracts', subtitle: `${contracts.length} contract${contracts.length !== 1 ? 's' : ''}` },
        { label: 'Invoices', icon: ReceiptIcon, path: '/employer/invoices', subtitle: `${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}` },
        { label: 'Reports', icon: AssessmentIcon, path: '/employer/reports', subtitle: 'View analytics' },
    ], [jobs.length, applications.length, employees.length, salarySpecs.length, contracts.length, invoices.length]);

    if (companyLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!company) {
        return (
            <Alert
                severity="warning"
                action={
                    <Button href="/employer/my-company" size="small" color="inherit">
                        Set up company
                    </Button>
                }
            >
                You need to create a company profile first.
            </Alert>
        );
    }

    return (
        <Box>
            {/* Charts — row 1 */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} lg={6}>
                    <OverviewStatsChart
                        jobs={jobs.length}
                        applications={applications.length}
                        salarySpecs={salarySpecs.length}
                        invoices={invoices.length}
                        contracts={contracts.length}
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <JobsVsApplicationsChart jobs={jobs} applications={applications} />
                </Grid>
            </Grid>

            {/* Charts — row 2 */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {invoices.length > 0 && (
                    <Grid item xs={12} md={4}>
                        <InvoiceStatusChart invoices={invoices} />
                    </Grid>
                )}
                {jobs.length > 0 && (
                    <Grid item xs={12} md={invoices.length > 0 ? 4 : 6}>
                        <MonthlyJobsChart jobs={jobs} />
                    </Grid>
                )}
                {applications.length > 0 && (
                    <Grid item xs={12} md={invoices.length > 0 ? 4 : 6}>
                        <ApplicationsOverTimeChart applications={applications} />
                    </Grid>
                )}
            </Grid>

            {/* Quick Access */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                Quick Access
            </Typography>
            <Grid container spacing={2}>
                {quickLinks.map(({ label, icon: Icon, path, subtitle }) => (
                    <Grid item xs={12} sm={6} lg={3} key={path}>
                        <Card
                            component={Link}
                            to={path}
                            elevation={0}
                            sx={{
                                display: 'block',
                                height: '100%',
                                textDecoration: 'none',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                transition: 'border-color 0.15s, box-shadow 0.15s',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                }
                            }}
                        >
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2,
                                        bgcolor: 'primary.50',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 1.5
                                    }}
                                >
                                    <Icon sx={{ fontSize: 22, color: 'primary.main' }} />
                                </Box>
                                <Typography variant="body1" fontWeight={600} sx={{ color: 'text.primary', mb: 0.25 }}>
                                    {label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {subtitle}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

export default EmployerDashboard;
